// src/utils/bodyState.js
//
// Single source of truth for "why is this body missing?" across the request
// detail pane (InspectraNetworkView) and the validators (validators.jsx).
//
// A row's request/response body is in exactly one of three states, and the two
// missing states are NOT the same failure — conflating them misleads the user:
//
//   present : the bytes are here — render them.
//   trimmed : captured fine, but the dashboard keeps full bodies only for the
//             newest FULL_BODY_KEEP rows per device (see MainApp.js) and stripped
//             this older row's body to bound browser memory. The row carries
//             `_bodyStripped: true`. The full bytes still exist upstream (proxy
//             live capture / HAR store) — this is a UI retention limit, not a
//             capture miss.
//   absent  : the proxy genuinely has no body — a gzip/brotli decode failure, a
//             QUIC/HTTP-3 response MITM can't read, or a plain capture miss. The
//             request still fired.
//
// Before this helper, every "missing body" was reported as "gzip/QUIC or proxy
// miss", so a trimmed-for-memory body (recoverable) looked identical to a real
// capture gap (not recoverable). Keep this the ONLY place that phrases it.

/**
 * @param {object} row   a network row (live event or history entry)
 * @param {'request'|'response'} which  which body to inspect
 * @returns {{ state: 'present'|'trimmed'|'absent', message: string }}
 *          `message` is '' for the present state.
 */
export function bodyState(row, which) {
  const body = which === 'request'
    ? (row?.requestBody ?? row?.requestBodyParsed)
    : row?.responseBody;

  const present = body != null
    && !(typeof body === 'string' && body.trim() === '');
  if (present) return { state: 'present', message: '' };

  // Ingested rows (adb logcat tap -> POST /proxy/ingest/rows) never passed
  // through the proxy: the device logged the URL, nothing more. There is no
  // body to have missed, so don't guess at gzip/QUIC.
  if (row && row.captureSource) {
    const src = row.captureSource === 'logcat' ? 'the device log (adb logcat)' : `an external source (${row.captureSource})`;
    return {
      state: 'absent',
      message:
        `Request-only capture — this URL came from ${src}, not through the proxy, so no ` +
        'request/response body exists. URL params are validated; response-based checks do not apply.',
    };
  }

  // Captured, then trimmed. Two distinct trim sources, both recoverable —
  // don't conflate with a real capture miss:
  //   true   : two-tier in-memory retention (older than FULL_BODY_KEEP).
  //   'disk' : proxy-side history persistence (saveHistory) strips bodies
  //            before writing to disk, then stamps `_bodyStripped: 'disk'`
  //            on reload (WP1/B10) so a disk-reloaded row still reads as
  //            "trimmed", not a capture failure.
  if (row && row._bodyStripped) {
    const fromDisk = row._bodyStripped === 'disk';
    return {
      state: 'trimmed',
      message: fromDisk
        ? 'Body trimmed on disk reload — the persisted history store keeps request/response ' +
          'metadata only, not full bodies. It was captured fine; re-capture it live, or open ' +
          'it from the HAR store, to see the full bytes.'
        : 'Body trimmed to save memory — full request/response bodies are kept only ' +
          'for the most recent requests, and this row has scrolled past that window. ' +
          'It was captured fine; re-capture it live, or open it from the HAR store, to see the full bytes.',
    };
  }

  // Media/streaming responses (HLS/DASH manifests + segments, video/audio) are
  // intentionally NOT buffered — they're large binary content, not useful to
  // store and would balloon memory. Say so honestly instead of implying a
  // gzip/QUIC decode failure.
  if (which === 'response' && isMediaResponse(row)) {
    return {
      state: 'absent',
      message:
        'Media/streaming response body — not stored. Video/audio segments and manifests ' +
        'are large binary content the proxy doesn\'t buffer. The request still fired, and its ' +
        'status + headers are captured.',
    };
  }

  // The origin sent nothing: an empty response is a fact, not a capture miss.
  // (responseBytes is stamped by the proxy since 2026-10-18; older rows lack it.)
  if (which === 'response' && row && row.responseBytes === 0 && !row.responseBodyTruncated) {
    return {
      state: 'absent',
      message:
        'Empty response — the server sent 0 bytes, so there was nothing to capture. ' +
        'For a GAM ad request with no impression or fill report after it, this is what no ad returned looks like.',
    };
  }

  // Genuinely no body from the proxy.
  const noun = which === 'request' ? 'Request' : 'Response';
  return {
    state: 'absent',
    message:
      `${noun} body not captured — likely a gzipped/brotli body the proxy couldn't ` +
      'decode, a QUIC/HTTP-3 response MITM can\'t read, or a capture miss. The request still fired.',
  };
}

/** Heuristic: is this row a media/streaming response whose body we deliberately
 *  don't buffer (HLS/DASH manifests + segments, video/audio)? */
function isMediaResponse(row) {
  const ct = String(row?.responseContentType || '').toLowerCase();
  if (/video\/|audio\/|mpegurl|dash\+xml|mp2t|octet-stream/.test(ct)) return true;
  if (String(row?.requestType || '').toLowerCase() === 'video_content') return true;
  const path = String(row?.url || '').toLowerCase().split('?')[0];
  if (/\.(ts|m3u8|mpd|mp4|m4s|aac|cmfv|cmfa)$/.test(path)) return true;
  return false;
}

/** True when a request of this method is expected to carry a body at all
 *  (so we don't nag "request body not captured" on a plain GET beacon). */
export function methodExpectsBody(method) {
  return /^(POST|PUT|PATCH)$/i.test(String(method || '').trim());
}
